import { DEMO_CREDENTIALS, DEMO_INVITABLE_ROLES } from '@/api/demoAuth'
import { ApiError } from '@/api/errors'
import type { InviteInput } from '@/features/invitations/schemas/invitationSchemas'
import type { AcceptedInvitation, InvitationPreview } from '@/features/auth/types'
import type { InvitationCreated } from '@/features/invitations/types'

/**
 * Open invitations by token. In memory only, so a demo link works in the tab
 * that created it and is gone after a reload — the real one lives 72 hours.
 */
const invitations = new Map<string, InviteInput>()

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** The backend answers every unusable token the same way, on purpose. */
const notFound = () => new ApiError({ message: 'Nicht gefunden.', status: 404, code: 'NOT_FOUND' })

export const demoInvitations = {
  fetchRoles: async () => {
    await wait(200)
    return DEMO_INVITABLE_ROLES
  },

  create: async (input: InviteInput): Promise<InvitationCreated> => {
    await wait(400)
    const email = input.email.trim().toLowerCase()

    if (email === DEMO_CREDENTIALS.email) {
      throw new ApiError({
        message: 'Für diese E-Mail-Adresse besteht bereits ein Konto.',
        status: 409,
        code: 'EMAIL_TAKEN',
      })
    }

    // Inviting the same address again replaces the earlier link.
    for (const [token, invitation] of invitations) {
      if (invitation.email === email) invitations.delete(token)
    }

    const token = crypto.randomUUID()
    invitations.set(token, { ...input, email })

    return {
      publicId: crypto.randomUUID(),
      email,
      fullName: input.fullName,
      role: input.role,
      expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
      token,
    }
  },

  preview: async (token: string): Promise<InvitationPreview> => {
    await wait(300)
    const invitation = invitations.get(token)
    if (!invitation) throw notFound()

    return {
      email: invitation.email,
      fullName: invitation.fullName,
      locationName: 'Studio München',
      roleLabel:
        DEMO_INVITABLE_ROLES.find((role) => role.key === invitation.role)?.label ?? invitation.role,
    }
  },

  accept: async (token: string): Promise<AcceptedInvitation> => {
    await wait(400)
    const invitation = invitations.get(token)
    if (!invitation) throw notFound()

    invitations.delete(token)
    return { publicId: crypto.randomUUID(), email: invitation.email }
  },
}
